import {
  documentPipelineFilterSchema,
  sourceTypeFilterSchema,
  type DocumentPipelineFilter,
} from '@kanbu/schema/contracts';
import { DocumentStatus, type SourceType } from '@kanbu/schema/enums';
import { useLingui, Trans } from '@lingui/react/macro';
import {
  createFileRoute,
  useNavigate,
  type SearchSchemaInput,
} from '@tanstack/react-router';
import { Button } from '@utima/ui';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { SimpleTooltip } from '@/components';
import type { BreadCrumbItem } from '@/components/breadCrumb/BreadCrumb';
import { ResourcePage } from '@/components/page/ResourcePage';
import { AddDocumentsModal } from '@/features/documents/components/addDocumentsModal/AddDocumentsModal';
import { AutoUpdateSettingsModal } from '@/features/documents/components/AutoUpdateSettingsModal';
import { DocumentExportModal } from '@/features/documents/components/DocumentExportModal';
import { useDocumentMutation } from '@/features/documents/hooks/useDocumentMutation';
import { AccessPropagationBanner } from '@/features/knowledge-base/components/AccessPropagationBanner';
import { BulkAgentAccessModal } from '@/features/knowledge-base/components/BulkAgentAccessModal';
import { BulkAutoUpdateModal } from '@/features/knowledge-base/components/BulkAutoUpdateModal';
import { BulkWeightModal } from '@/features/knowledge-base/components/BulkWeightModal';
import { CreateFolderModal } from '@/features/knowledge-base/components/CreateFolderModal';
import { DeleteFoldersModal } from '@/features/knowledge-base/components/DeleteFoldersModal';
import { FolderSettingsModal } from '@/features/knowledge-base/components/FolderSettingsModal';
import { KBContentsSection } from '@/features/knowledge-base/components/KBContentsSection';
import { KBContentTable } from '@/features/knowledge-base/components/KBContentTable';
import { KBStatsSummary } from '@/features/knowledge-base/components/KBStatsSummary';
import { KBToolbar } from '@/features/knowledge-base/components/KBToolbar';
import type {
  KBDocumentData,
  KBFolderData,
} from '@/features/knowledge-base/components/kbTypes';
import { PromoteToTrackedWebsiteModal } from '@/features/knowledge-base/components/PromoteToTrackedWebsiteModal';
import { SourceStatusHeader } from '@/features/knowledge-base/components/SourceStatusHeader';
import { useKBContents } from '@/features/knowledge-base/hooks/useKBContents';
import { useKBMutations } from '@/features/knowledge-base/hooks/useKBMutations';
import { useKBSelection } from '@/features/knowledge-base/hooks/useKBSelection';
import { isManagedSource } from '@/features/knowledge-base/lib/managedSource';
import {
  type KBWebsitePageFilter,
  parseKBWebsitePageFilter,
} from '@/features/knowledge-base/lib/pageFilter';
import { useSubscriptionFeatures } from '@/hooks/useSubscriptionFeatures';
import { trpc } from '@/services/trpc';

export const Route = createFileRoute('/_baseLayout/knowledge-base/')({
  component: KnowledgeBasePage,
  validateSearch: (
    search: {
      folderId?: string;
      status?: string;
      documentPipelineFilter?: string;
      sourceType?: string;
    } & SearchSchemaInput,
  ) => ({
    folderId: search?.folderId as string | undefined,
    status: parseKBWebsitePageFilter(search?.status),
    documentPipelineFilter: (() => {
      const parsed = documentPipelineFilterSchema.safeParse(
        search?.documentPipelineFilter,
      );

      return parsed.success ? parsed.data : undefined;
    })(),
    sourceType: (() => {
      const parsed = sourceTypeFilterSchema.safeParse(search?.sourceType);

      return parsed.success ? parsed.data : undefined;
    })(),
  }),
});

function KnowledgeBasePage() {
  const { t } = useLingui();
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const {
    folderId,
    status: statusFilter,
    documentPipelineFilter,
    sourceType: sourceTypeFilter,
  } = Route.useSearch();
  const selection = useKBSelection();
  const mutations = useKBMutations();
  const { canUseKbAutoUpdates, isKbAutoUpdatesLocked } =
    useSubscriptionFeatures();

  // Clear selection when navigating between folders
  useEffect(() => {
    selection.clearSelection();
  }, [folderId]); // eslint-disable-line react-hooks/exhaustive-deps

  const [search, setSearch] = useState('');

  const setStatusFilter = useCallback(
    (status: KBWebsitePageFilter | undefined) => {
      const next =
        status !== undefined && status === statusFilter ? undefined : status;

      navigate({
        to: '/knowledge-base',
        search: {
          folderId,
          status: next,
          documentPipelineFilter: undefined,
          sourceType: sourceTypeFilter,
        },
      } as never);
    },
    [folderId, navigate, sourceTypeFilter, statusFilter],
  );
  const setDocumentPipelineFilter = useCallback(
    (filter: DocumentPipelineFilter | undefined) => {
      const next =
        filter !== undefined && filter === documentPipelineFilter
          ? undefined
          : filter;

      navigate({
        to: '/knowledge-base',
        search: {
          folderId,
          status: undefined,
          documentPipelineFilter: next,
          sourceType: sourceTypeFilter,
        },
      } as never);
    },
    [documentPipelineFilter, folderId, navigate, sourceTypeFilter],
  );
  // Source lens (master plan §1A): orthogonal to status/documentPipelineFilter
  // — it narrows by origin, not processing state, so it composes with either
  // instead of resetting them.
  const setSourceTypeFilter = useCallback(
    (sourceType: SourceType | undefined) => {
      const next =
        sourceType !== undefined && sourceType === sourceTypeFilter
          ? undefined
          : sourceType;

      navigate({
        to: '/knowledge-base',
        search: {
          folderId,
          status: statusFilter,
          documentPipelineFilter,
          sourceType: next,
        },
      } as never);
    },
    [
      documentPipelineFilter,
      folderId,
      navigate,
      sourceTypeFilter,
      statusFilter,
    ],
  );
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [showAddDocuments, setShowAddDocuments] = useState(false);
  const [showPromoteTrackedWebsite, setShowPromoteTrackedWebsite] =
    useState(false);
  const [settingsFolderId, setSettingsFolderId] = useState<string | null>(null);
  const [settingsDefaultTab, setSettingsDefaultTab] = useState<
    'general' | 'auto-update' | 'agents'
  >('general');

  const handleFolderSettings = useCallback(
    (
      targetFolderId?: string,
      tab: 'general' | 'auto-update' | 'agents' = 'general',
    ) => {
      const id = targetFolderId ?? folderId;

      if (!id) {
        return;
      }

      setSettingsDefaultTab(tab);
      setSettingsFolderId(id);
    },
    [folderId],
  );

  const contents = useKBContents({
    folderId: folderId ?? null,
    search,
    statusFilter,
    documentPipelineFilter,
    sourceTypeFilter,
    selection,
    mutations,
    onFolderSettings: handleFolderSettings,
  });

  // Fetch current folder details (if viewing a subfolder)
  const { data: currentFolder, isError: isFolderError } =
    trpc.documentFolders.findOne.useQuery(
      { id: folderId! },
      {
        enabled: !!folderId,
        retry: false,
        throwOnError: false,
        refetchOnMount: 'always',
      },
    );

  // If URL points to a deleted/non-existing folder, recover by
  // navigating to root instead of surfacing repeated query errors.
  useEffect(() => {
    if (isFolderError) {
      navigate({ to: '/knowledge-base', search: {} });
    }
  }, [isFolderError, navigate]);

  // Fetch breadcrumbs
  const { data: breadcrumbsData } =
    trpc.documentFolders.getBreadcrumbs.useQuery(
      { folderId: folderId! },
      {
        enabled: !!folderId,
        retry: false,
        throwOnError: false,
      },
    );

  // Fetch document stats for the status summary
  const { data: docStats } = trpc.documents.getStats.useQuery(undefined, {
    refetchOnMount: 'always',
    refetchInterval: query => {
      const stats = query.state.data;

      return stats &&
        stats.uploading +
          stats.ready +
          stats.parsing +
          stats.parsingAsync +
          stats.parsed +
          stats.embedding >
          0
        ? 5000
        : false;
    },
  });

  const docMutations = useDocumentMutation();

  // Fetch all chats for AddDocumentsModal
  const { data: allChats } = trpc.chats.findAll.useQuery({
    pagination: { pageSize: 100 },
    fields: ['id', 'name'],
  });

  // Build breadcrumb items
  const breadcrumbs = useMemo<BreadCrumbItem[]>(() => {
    const items: BreadCrumbItem[] = [
      { label: t`Knowledge Base`, to: '/knowledge-base' },
    ];

    if (breadcrumbsData) {
      for (const crumb of breadcrumbsData) {
        items.push({
          label: crumb.name,
          to: '/knowledge-base',
          search: { folderId: crumb.id },
        });
      }
    }

    return items;
  }, [breadcrumbsData, t]);

  const handleDeleteFolder = useCallback(() => {
    if (!folderId) {
      return;
    }

    contents.requestDeleteFolder(folderId, {
      onDeleted: async () => {
        const parentId =
          breadcrumbsData && breadcrumbsData.length > 1
            ? breadcrumbsData.at(-2)?.id
            : undefined;

        navigate({
          to: '/knowledge-base',
          search: parentId ? { folderId: parentId } : {},
        });

        await utils.documentFolders.findContents.invalidate({
          folderId: parentId ?? null,
        });
      },
    });
  }, [
    breadcrumbsData,
    contents.requestDeleteFolder,
    folderId,
    navigate,
    utils,
  ]);

  // Handle org-wide retry failed documents
  const handleRetryFailed = useCallback(async () => {
    await docMutations.refreshAllByOrg.mutateAsync({
      statuses: [DocumentStatus.EmbeddingError, DocumentStatus.ParsingError],
    });
  }, [docMutations]);

  const hasErrors =
    (docStats?.parsingError ?? 0) + (docStats?.embeddingError ?? 0) > 0;

  const selectedWebsiteDocuments = useMemo(() => {
    const selectedIds = new Set(selection.selectedItems.keys());

    return contents.rows
      .filter(
        row =>
          row.rowType === 'document' &&
          selectedIds.has(row.id) &&
          row.data.type === 'website',
      )
      .map(row => row.data as KBDocumentData);
  }, [contents.rows, selection.selectedItems]);

  // Get title: current folder name or "Knowledge Base"
  const title = currentFolder?.name ?? t`Knowledge Base`;

  const isManaged = isManagedSource(currentFolder);

  return (
    <ResourcePage
      title={title}
      breadcrumbs={breadcrumbs}
      actions={
        folderId ? undefined : (
          <div className='flex items-center gap-2'>
            <AutoUpdateSettingsModal />
            {hasErrors && (
              <SimpleTooltip title={t`Retry all failed documents`}>
                <Button
                  size='sm'
                  variant='danger'
                  loading={docMutations.refreshAllByOrg.isPending}
                  disabled={docMutations.refreshAllByOrg.isPending}
                  onClick={handleRetryFailed}
                >
                  <Trans>Retry Failed</Trans>
                </Button>
              </SimpleTooltip>
            )}
          </div>
        )
      }
    >
      {docStats && (
        <KBStatsSummary
          stats={docStats}
          onStatusFilterChange={setStatusFilter}
          onDocumentPipelineFilterChange={setDocumentPipelineFilter}
          activeDocumentPipelineFilter={documentPipelineFilter}
          activeStatusFilter={statusFilter}
        />
      )}

      <SourceStatusHeader
        folder={(currentFolder as KBFolderData | undefined) ?? null}
        mutations={mutations}
      />

      <KBContentsSection
        toolbar={
          <KBToolbar
            folderId={folderId ?? null}
            currentFolder={(currentFolder as KBFolderData | undefined) ?? null}
            rows={contents.rows}
            selection={selection}
            mutations={mutations}
            search={search}
            onSearchChange={setSearch}
            onCreateFolder={() => setShowCreateFolder(true)}
            onAddDocuments={() => setShowAddDocuments(true)}
            onDeleteFolder={handleDeleteFolder}
            onFolderSettings={handleFolderSettings}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
            documentPipelineFilter={documentPipelineFilter}
            onDocumentPipelineFilterChange={setDocumentPipelineFilter}
            sourceTypeFilter={sourceTypeFilter}
            onSourceTypeFilterChange={setSourceTypeFilter}
            isManagedSource={isManaged}
            onBulkAgentAccess={contents.handleBulkAgentAccess}
            onBulkAutoUpdate={contents.handleBulkAutoUpdate}
            isBulkAutoUpdateLocked={isKbAutoUpdatesLocked}
            onBulkWeight={contents.handleBulkWeight}
            onPromoteToTrackedWebsite={() => setShowPromoteTrackedWebsite(true)}
            onExcludeFromKB={contents.handleExcludeFromKB}
            onRequestDeleteFolders={contents.requestDeleteFolders}
            onViewDocument={contents.handleSelectDocument}
            onExportDocuments={contents.handleOpenExport}
          />
        }
      >
        <AccessPropagationBanner folderId={folderId} />
        <KBContentTable
          rows={contents.rows}
          contentUpdatedSort={contents.contentUpdatedSort}
          onContentUpdatedSortChange={contents.setContentUpdatedSort}
          folderId={folderId ?? null}
          loading={contents.isLoading}
          selection={selection}
          selectedDocumentId={contents.selectedDocumentId}
          onSelectDocument={contents.handleSelectDocument}
          defaultTab={contents.defaultTab}
          columnActions={contents.columnActions}
          folderPaths={contents.folderPaths}
          isManagedSource={isManaged}
          onSearchReset={() => {
            setSearch('');
            setStatusFilter(undefined);
            setDocumentPipelineFilter(undefined);
            setSourceTypeFilter(undefined);
          }}
          canUseAutoUpdate={canUseKbAutoUpdates}
          hasMore={contents.hasNextPage}
          isLoadingMore={contents.isFetchingNextPage}
          onLoadMore={() => contents.fetchNextPage()}
        />
      </KBContentsSection>

      <CreateFolderModal
        open={showCreateFolder}
        onOpenChange={setShowCreateFolder}
        parentId={folderId ?? null}
        mutations={mutations}
      />

      {contents.folderDeletePrompt && (
        <DeleteFoldersModal
          open
          onOpenChange={open => {
            if (!open) {
              contents.clearFolderDeletePrompt();
            }
          }}
          folders={
            contents.folderDeletePrompt.mode === 'single'
              ? [contents.folderDeletePrompt.folder]
              : contents.folderDeletePrompt.folders
          }
          mutations={mutations}
          onDeleted={async () => {
            const onDeleted = contents.folderDeletePrompt?.onDeleted;

            contents.clearFolderDeletePrompt();
            selection.clearSelection();
            await onDeleted?.();
          }}
        />
      )}

      {settingsFolderId && (
        <FolderSettingsModal
          open={!!settingsFolderId}
          onOpenChange={open => {
            if (!open) {
              setSettingsFolderId(null);
              setSettingsDefaultTab('general');
            }
          }}
          folderId={settingsFolderId}
          defaultTab={settingsDefaultTab}
          mutations={mutations}
        />
      )}

      <AddDocumentsModal
        allChats={allChats?.items ?? []}
        folderId={folderId}
        externalOpen={showAddDocuments}
        onExternalOpenChange={setShowAddDocuments}
      />

      <PromoteToTrackedWebsiteModal
        open={showPromoteTrackedWebsite}
        onOpenChange={setShowPromoteTrackedWebsite}
        documents={selectedWebsiteDocuments}
        mutations={mutations}
        onFinished={() => selection.clearSelection()}
      />

      <BulkAgentAccessModal
        open={contents.isBulkAgentAccessOpen}
        onOpenChange={contents.setIsBulkAgentAccessOpen}
        selectedRows={contents.selectedSettingRows}
        mutations={mutations}
      />

      <BulkAutoUpdateModal
        open={contents.isBulkAutoUpdateOpen}
        onOpenChange={contents.setIsBulkAutoUpdateOpen}
        selectedRows={contents.selectedSettingRows}
        mutations={mutations}
      />

      {contents.exportSelection.length > 0 && (
        <DocumentExportModal
          documents={contents.exportSelection}
          onClose={contents.handleCloseExport}
        />
      )}

      <BulkWeightModal
        open={contents.isBulkWeightOpen}
        onOpenChange={contents.setIsBulkWeightOpen}
        selectedRows={contents.selectedSettingRows}
        mutations={mutations}
      />
    </ResourcePage>
  );
}
